import { ApolloClient, ErrorLike, MaybeMasked, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useBoolean } from 'minimal-shared/hooks/use-boolean';
import { useEffect } from 'react';

export type UseQueryResult<TData, TVariables extends OperationVariables> = {
  /** Result of the query after it completes; `undefined` while loading or on error. */
  data: MaybeMasked<TData> | undefined;
  /** Previous successful result, for keeping data visible during refetches. */
  previousData: MaybeMasked<TData> | undefined;
  /** True while the query is in flight. */
  loading: boolean;
  /** Re-execute the query, optionally passing new variables. */
  refetch: (variables?: Partial<TVariables> | undefined) => Promise<ApolloClient.QueryResult<MaybeMasked<TData>>>;
  /** Apollo v4 unified error (covers GraphQL, protocol and network errors). */
  error: ErrorLike | undefined;
  /** True only on the very first load — used to distinguish initial vs. background fetches. */
  firstLoading: boolean;
};

/**
 * Generic wrapper around `useQuery` that defaults to a cache-and-network fetch policy and
 * surfaces a `firstLoading` flag for first-paint vs. background-refresh logic.
 */
export function useFetchGeneric<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: Omit<useQuery.Options<TData, TVariables>, 'variables'>,
): UseQueryResult<TData, TVariables> {
  const { data, loading, refetch, error, previousData } = useQuery(document, {
    variables: variables as TVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    ...options,
  });

  const firstLoading = useBoolean(true);
  useEffect(() => {
    if (loading === false && firstLoading.value === true) {
      firstLoading.onFalse();
    }
  }, [loading, firstLoading]);

  return {
    data,
    previousData,
    loading,
    firstLoading: firstLoading.value,
    refetch,
    error,
  };
}
